// Affichage en anglais : l'interface est écrite en français ; quand l'utilisateur choisit l'anglais,
// chaque texte affiché (ou placeholder / title) qui correspond EXACTEMENT à une entrée du dictionnaire
// est remplacé par sa traduction. Les valeurs techniques (PENDING, FOR_RENT…) sont rendues lisibles.
import { getLanguage } from './i18n';
import EN1 from './enDictionary1';
import EN2 from './enDictionary2';
import EN3 from './enDictionary3';

const EXTRA = {
  '⬇ Exporter (Excel)': '⬇ Export (Excel)', 'Exporter (Excel)': 'Export (Excel)', 'Rechercher': 'Search', 'Rechercher…': 'Search…',
  'Rechercher...': 'Search…', 'Détails': 'Details', 'Confirmer': 'Confirm', 'Paramètres': 'Settings', 'Tableau de bord': 'Dashboard',
  'Paiements': 'Payments', 'Paiement': 'Payment', 'Contrat de bail': 'Lease contract', 'Erreur': 'Error', 'Filtres': 'Filters', 'Filtrer': 'Filter',
  'Aucune donnée': 'No data', 'Aucune donnée.': 'No data.', 'Partiellement occupé': 'Partially occupied', 'Entièrement occupé': 'Fully occupied',
  'Brouillon': 'Draft', 'Validé': 'Approved', 'Annulé': 'Cancelled', 'Confirmé': 'Confirmed', 'Expiré': 'Expired', 'Résilié': 'Terminated',
  'Partiel': 'Partial', 'À venir': 'Upcoming', 'À payer': 'Due', 'Remboursé': 'Refunded', 'Signé': 'Signed', 'Envoyé': 'Sent', 'Échec': 'Failed',
  'Réussi': 'Succeeded', 'Archivé': 'Archived', 'Soumis': 'Submitted', 'Ouvert': 'Open', 'Clôturé': 'Closed', 'Résolu': 'Resolved', 'Assigné': 'Assigned',
  'Non assigné': 'Unassigned', 'Loué': 'Rented', 'Vendu': 'Sold', 'Réservé': 'Reserved', 'En travaux': 'Under works', 'Indisponible': 'Unavailable',
  'Publié': 'Published', 'Maison': 'House', 'Bureau': 'Office', 'Magasin': 'Shop', 'Terrain': 'Land', 'Studio': 'Studio', 'Location': 'Rental', 'Vente': 'Sale',
  'Mensuel': 'Monthly', 'Annuel': 'Yearly', 'Trimestriel': 'Quarterly', 'Tous': 'All', 'Toutes': 'All', 'Carte bancaire': 'Bank card',
  'Imputation d’avance': 'Advance allocation', 'Directement au propriétaire': 'Paid directly to the owner', 'Payé directement au propriétaire': 'Paid directly to the owner',
  'En attente de validation': 'Awaiting approval', 'En attente de signature': 'Awaiting signature', 'En attente de la direction': 'Awaiting management',
  'Langue': 'Language', 'Mon profil': 'My profile', 'Notifications': 'Notifications', 'Déconnexion': 'Sign out'
};

const norm = (s) => String(s).replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim();
const DICT = new Map();
for (const src of [EN1, EN2, EN3, EXTRA]) for (const [fr, en] of Object.entries(src)) DICT.set(norm(fr), en);

const ENUM_WORDS = new Set(['PENDING', 'APPROVED', 'VALIDATED', 'REJECTED', 'CANCELLED', 'CONFIRMED', 'ACTIVE', 'INACTIVE', 'ARCHIVED', 'DRAFT',
  'SUBMITTED', 'OPEN', 'CLOSED', 'COMPLETED', 'RESOLVED', 'ASSIGNED', 'SCHEDULED', 'SENT', 'SIGNED', 'EXPIRED', 'TERMINATED', 'PAID', 'UNPAID',
  'PARTIAL', 'OVERDUE', 'LATE', 'REFUNDED', 'CASH', 'CHEQUE', 'AVAILABLE', 'OCCUPIED', 'VACANT', 'RESERVED', 'RENTED', 'SOLD', 'BUILDING',
  'APARTMENT', 'VILLA', 'HOUSE', 'STUDIO', 'DUPLEX', 'OFFICE', 'SHOP', 'LAND', 'URGENT', 'HIGH', 'MEDIUM', 'NORMAL', 'LOW', 'EXHAUSTED', 'FINISHED']);
const humanizeEnum = (s) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/** Traduction anglaise d'un texte de l'interface ; null si inconnue. */
export function enLabel(value) {
  const raw = norm(value ?? '');
  if (!raw || raw.length > 160) return null;
  if (DICT.has(raw)) return DICT.get(raw);
  // « Libellé : » / « Champ * » : on traduit le libellé et on garde la ponctuation
  const m = raw.match(/^(.*?)(\s*[:*]+)$/);
  if (m && DICT.has(norm(m[1]))) return DICT.get(norm(m[1])) + m[2].replace(/^\s+/, '');
  if (/^[A-Z]{3,}(_[A-Z]+)+$/.test(raw) || ENUM_WORDS.has(raw)) return humanizeEnum(raw);
  return null;
}

const SKIP = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE']);
const ATTRS = ['placeholder', 'title', 'aria-label'];
const blocked = (el) => !el || SKIP.has(el.nodeName) || el.isContentEditable || el.closest?.('[data-no-translate]');

function translateText(node) {
  if (blocked(node.parentNode)) return;
  const text = node.nodeValue;
  if (!text || !/[A-Za-zÀ-ÿ]/.test(text)) return;
  const en = enLabel(text);
  if (en && norm(text) !== en) node.nodeValue = text.replace(text.trim(), en);
}

function translateAttrs(el) {
  if (blocked(el)) return;
  for (const a of ATTRS) {
    const v = el.getAttribute?.(a);
    if (!v) continue;
    const en = enLabel(v);
    if (en && en !== v) el.setAttribute(a, en);
  }
}

function walk(root) {
  if (root.nodeType === 3) { translateText(root); return; }
  if (root.nodeType !== 1 || SKIP.has(root.nodeName)) return;
  translateAttrs(root);
  root.querySelectorAll?.('[placeholder],[title],[aria-label]').forEach(translateAttrs);
  const it = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = it.nextNode(); n; n = it.nextNode()) translateText(n);
}

let observer = null;
/** Active la traduction à l'affichage (langue anglaise uniquement ; changer de langue recharge la page). */
export function installEnDisplay() {
  if (observer || typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
  const start = () => {
    if (getLanguage() !== 'en') return;
    document.documentElement.lang = 'en';
    walk(document.body);
    observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'characterData') translateText(m.target);
        else if (m.type === 'attributes') translateAttrs(m.target);
        else m.addedNodes.forEach(walk);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  };
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
}
